/* eslint-disable max-len */
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import { useFormik } from 'formik';
import styled, { css } from 'styled-components';
import * as yup from 'yup';
import { AlertHeader, Button, DateRangePicker, Message, ModalFactory, ModalTitle as ModalTitleBase, Tag, Text } from 'components';
import { lineHeight, theme, toRem } from 'design';
import { Appearance } from 'design/types';
import { selectIsLoadingAPIRequest } from 'store/ApiClient';
import { generateHealthReportRequest, Site } from 'store/Site';
import { IStore } from 'store/types';
import { HealthReportSectionName, IHealthReport } from 'types/HealthReport';
import { centsToDollarsDisplay, Duration, formatDateForApi } from 'utils';
import { LoadingText as LoadingTextBase, ModalContent, StyledSpinner } from '../../shared/styles';

const Content = styled(ModalContent)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${toRem(theme.space.lg)};
`;

const ModalSubtitle = styled(Text)`
  text-align: center;
  margin-top: ${toRem(theme.space.lg)};
`;

const ModalTitle = styled(ModalTitleBase)`
  font-size: ${toRem(theme.font.size.lg)};
  line-height: ${lineHeight(theme.font.size.lg)};
  margin-bottom: ${toRem(theme.space.sm)};
`;

const HeaderContainer = styled.div`
  display: block;
`;

const ContinueButton = styled(Button)`
  width: 100%;
  margin-top: ${toRem(theme.space.xl)};
`;

const ErrorText = styled(Text)`
  text-align: center;
`;

const LoadingText = styled(LoadingTextBase)`
  font-size: ${toRem(theme.font.size.md)};
  line-height: ${lineHeight(theme.font.size.md)};
`;

const DatePickerContainer = styled.div`
  display: flex;
  align-items: center;
`;

const ExternalContainer = styled.div`
  display: block;
  border-radius: ${toRem(5.5)};
  border: ${toRem(1)} solid ${theme.color.neutral[200]};
  padding: ${toRem(theme.space.lg)};
  margin-top: ${toRem(theme.space.lg)};
  position: relative;
`;

const InternalContainer = styled.div`
  display: block;
  background-color: ${theme.color.neutral[50]};
  padding: ${toRem(theme.space.md)};
  border-radius: ${toRem(8)};
  margin-top: ${toRem(theme.space.md)};
`;

const InternalTitle = styled.p`
  color: ${theme.color.neutral[900]};
  font-weight: ${theme.font.weight.semiBold};
  font-family: ${theme.font.family.euclid};
  font-size: ${toRem(theme.font.size.md)};
  line-height: ${lineHeight(theme.font.size.md)};
`;

const InternalSubtitle = styled.p`
  color: ${theme.color.neutral[500]};
  font-family: ${theme.font.family.euclid};
  font-size: ${toRem(theme.font.size.xs)};
  line-height: ${lineHeight(theme.font.size.xs)};
  margin-top: ${toRem(theme.space.xs)};
`;

const SubtitleEmphasis = styled.span`
  font-weight: ${theme.font.weight.semiBold};
`;

const BorderContainer = styled.div<{ border?: boolean; paddingTop?: boolean; paddingBottom?: boolean }>`
  display: block;
  ${(props) =>
    props.paddingTop &&
    css`
      padding-top: ${toRem(theme.space.sm)};
    `}
  ${(props) =>
    props.paddingBottom &&
    css`
      padding-bottom: ${toRem(theme.space.sm)};
    `}
  ${(props) =>
    props.border &&
    css`
      border-bottom: ${toRem(1)} solid ${theme.color.neutral[200]};
    `}
`;

const DetailsButton = styled.button`
  color: ${theme.color.primary[600]};
  font-family: ${theme.font.family.euclid};
  font-weight: 500;
  font-size: ${toRem(theme.font.size.xxs)};
  line-height: ${lineHeight(theme.font.size.xxs)};
  position: absolute;
  top: ${toRem(16)};
  right: ${toRem(16)};
`;

const reportTextStyles = css`
  font-family: ${theme.font.family.euclid};
`;

const SectionTitle = styled.p`
  ${reportTextStyles};
  color: ${theme.color.neutral[900]};
  font-weight: ${theme.font.weight.semiBold};
  font-size: ${toRem(theme.font.size.xs)};
  line-height: ${lineHeight(theme.font.size.xs)};
`;

const SubtextSmall = styled.p`
  ${reportTextStyles};
  color: ${theme.color.neutral[500]};
  font-size: ${toRem(theme.font.size.xxs)};
  line-height: ${lineHeight(theme.font.size.xxs)};
`;

const SubtextMedium = styled.p`
  ${reportTextStyles};
  color: ${theme.color.neutral[500]};
  font-size: ${toRem(theme.font.size.xs)};
  line-height: ${lineHeight(theme.font.size.xs)};
`;

const SpacedItemContainer = styled.div`
  display: flex;
  justify-content: space-between;
`;

const JobItemContainer = styled.div`
  display: flex;
  margin-top: ${toRem(theme.space['2xs'])};
  align-items: center;
  gap: ${toRem(theme.space.sm)};
`;

const SharedPosTag = styled(Tag)`
  /* off-scale exception: no font-size token below xxs (11px) */
  font-size: ${toRem(10)};
  padding: ${toRem(theme.space.sm)} ${toRem(theme.space.sm)};
  margin: -${toRem(theme.space.sm)} 0;
  height: auto;
`;

const JobGratAmount = styled(SubtextSmall)`
  margin-left: auto;
`;

const SalesCategoryItemContainer = styled(SpacedItemContainer)`
  margin-top: ${toRem(theme.space.xs)};
`;

const SalesCategoryLabel = styled.p`
  ${reportTextStyles};
  color: ${theme.color.neutral[500]};
  font-weight: ${theme.font.weight.semiBold};
  font-size: ${toRem(theme.font.size.xs)};
  line-height: ${lineHeight(theme.font.size.xs)};
`;

const SalesCategoryAmount = SubtextMedium;

const ChecksNoLaborItemContainer = styled(SpacedItemContainer)`
  margin-top: ${toRem(theme.space['2xs'])};
`;

const ChecksNoOwnerItemContainer = styled(SpacedItemContainer)`
  margin-top: ${toRem(theme.space.xs)};
`;

const CheckNoOwnerCheckId = styled.p`
  color: ${theme.color.neutral[500]};
  font-weight: ${theme.font.weight.semiBold};
  font-family: ${theme.font.family.euclid};
  font-size: ${toRem(theme.font.size.xs)};
  line-height: ${lineHeight(theme.font.size.xs)};
`;

const UnclosedTicketsItemContainer = styled(SpacedItemContainer)`
  margin-top: ${toRem(theme.space['2xs'])};
`;

const AutoClockoutsItemContainer = styled(SpacedItemContainer)`
  margin-top: ${toRem(theme.space['2xs'])};
`;

const AutoClockoutsHeader = styled(SectionTitle)`
  max-width: ${toRem(210)};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

type ModalType = 'generate' | 'result';

const schema = yup.object().shape({
  from: yup.string().required(),
  to: yup.string().required(),
});

const DEFAULT_START_DATE = dayjs(new Date()).subtract(7, 'day').format('YYYY-MM-DD');
const DEFAULT_END_DATE = dayjs(new Date()).subtract(1, 'day').format('YYYY-MM-DD');

interface IAIRulesModalProps {
  siteId: string;
  onClose(): void;
  dataCy?: string;
}

export const HealthReportModal: React.FC<IAIRulesModalProps> = ({ siteId, onClose, dataCy = 'health-report-modal' }) => {
  const form = useFormik({
    initialValues: {
      from: DEFAULT_START_DATE,
      to: DEFAULT_END_DATE,
    },
    validationSchema: schema,
    onSubmit() {
      dispatch(
        generateHealthReportRequest(
          siteId,
          {
            start: formatDateForApi(form.values.from),
            end: formatDateForApi(form.values.to),
          },
          (report) => setReport(report),
          handleError
        )
      );

      setModal('result');
    },
  });

  const [modal, setModal] = useState<ModalType>('generate');
  const [expanded, setExpanded] = useState<HealthReportSectionName>('jobs-section');
  const dispatch = useDispatch();
  const [reports, setReport] = useState<IHealthReport[]>(null);
  const [error, setError] = useState<any>(null);
  const isGeneratingHealthReport = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, Site.GENERATE_HEALTH_REPORT_REQUEST));

  const handleError = (error: any) => {
    setError(error);
  };

  const onToggleReportDetails = (value: HealthReportSectionName) => {
    setExpanded((currentValue) => (currentValue === value ? undefined : value));
  };

  const ticketsNoLabor = reports?.flatMap((report) => report.ticketsWithNoLabor.map((ticket) => ({ date: report.date, ...ticket })));

  const ticketsNoOwner = reports?.flatMap((report) => report.ticketsWithNoOwner.map((ticket) => ({ date: report.date, ...ticket })));

  const unclosedTickets = reports?.flatMap((report) => report.unclosedTickets.map((ticket) => ({ date: report.date, ...ticket })));

  const autoClockOuts = reports
    ?.flatMap((report) => report.autoClockedOutLabor)
    .flatMap((labor) =>
      labor.schedules.map((schedule) => ({
        firstName: labor.firstName,
        lastName: labor.lastName,
        schedule,
      }))
    );

  return (
    <ModalFactory dataCy={dataCy} onClose={onClose} isActive maxWidth={540} closeOnOutSideClick={false}>
      <Content>
        {modal === 'generate' && (
          <>
            <HeaderContainer>
              <ModalTitle>Health Report</ModalTitle>

              <ModalSubtitle size="small">Select the date you would like to use to generate the POS health report.</ModalSubtitle>
            </HeaderContainer>

            <form onSubmit={form.handleSubmit}>
              <DatePickerContainer>
                <DateRangePicker
                  className="report-date-range-picker"
                  data-cy="report-date-range-picker"
                  fluidWidth={false}
                  inputHeight="small"
                  pickerPosition="right"
                  submitLabel="apply"
                  maxDays={7}
                  startDate={form.values.from}
                  endDate={form.values.to}
                  onSubmit={([from, to]) => {
                    form.setFieldValue('from', from);
                    form.setFieldValue('to', to);
                  }}
                  setGlobalDateRange={false}
                />
              </DatePickerContainer>

              <ContinueButton type="submit" appearance="primary" disabled={!form.isValid} label="Generate Report" data-cy={`${dataCy}-generate-report-button`} />
            </form>
          </>
        )}

        {modal === 'result' && isGeneratingHealthReport && (
          <>
            <ModalTitle>Generating Health Report</ModalTitle>
            <StyledSpinner color={theme.color.secondary[400]} size={64} stroke={6} />
            <LoadingText>Please wait while we generate the report.</LoadingText>
          </>
        )}

        {modal === 'result' && !isGeneratingHealthReport && reports && !error && (
          <>
            <HeaderContainer>
              <ModalTitle>Health Report</ModalTitle>

              <ModalSubtitle size="small">
                The following is a POS Health report for <SubtitleEmphasis>{isoToUsDate(form.values.from)}</SubtitleEmphasis> to <SubtitleEmphasis>{isoToUsDate(form.values.to)}</SubtitleEmphasis>.
              </ModalSubtitle>

              <ExternalContainer data-cy="jobs-section">
                <InternalTitle>Jobs with Tips & Autograts</InternalTitle>

                <InternalSubtitle>
                  A total of <SubtitleEmphasis>{new Set(reports.flatMap((report) => report.jobs.filter((job) => job.gratuities.reduce((sum, gratuity) => sum + gratuity.amount, 0) > 0).map((job) => job.jobTitle))).size}</SubtitleEmphasis>{' '}
                  jobs titles were found that have collected either tips or autograts.
                </InternalSubtitle>

                <DetailsButton onClick={() => onToggleReportDetails('jobs-section')}>{expanded === 'jobs-section' ? 'Hide' : 'Show'} Details</DetailsButton>

                {expanded === 'jobs-section' && (
                  <InternalContainer>
                    {reports.map((report, index) => (
                      <BorderContainer key={report.date} border={index < reports.length - 1} paddingBottom={index < reports.length - 1} paddingTop={index > 0}>
                        <SectionTitle>{isoToUsDate(report.date)}</SectionTitle>

                        {report.jobs.filter((job) => job.gratuities.reduce((sum, gratuity) => sum + gratuity.amount, 0) > 0).length === 0 && <SubtextSmall>No data</SubtextSmall>}

                        {report.jobs
                          .filter((job) => job.gratuities.reduce((sum, gratuity) => sum + gratuity.amount, 0) > 0)
                          .map((job) => (
                            <JobItemContainer key={job.jobTitle}>
                              <SubtextSmall>{job.jobTitle}</SubtextSmall>
                              {job.isSharedPos && <SharedPosTag label="Shared POS" />}
                              <JobGratAmount>{centsToDollarsDisplay(job.gratuities.reduce((sum, gratuity) => sum + gratuity.amount, 0))}</JobGratAmount>
                            </JobItemContainer>
                          ))}
                      </BorderContainer>
                    ))}
                  </InternalContainer>
                )}
              </ExternalContainer>

              <ExternalContainer data-cy="sales-categories">
                <InternalTitle>Sales Category Totals</InternalTitle>

                <InternalSubtitle>
                  A total of <SubtitleEmphasis>{new Set(reports.flatMap((report) => report.salesCategories.filter((salesCategory) => salesCategory.total > 0).map((job) => job.name))).size}</SubtitleEmphasis> sales categories were found,
                  totalling {centsToDollarsDisplay(reports.flatMap((report) => report.salesCategories).reduce((sum, salesCategory) => sum + salesCategory.total, 0))} in sales.
                </InternalSubtitle>

                <DetailsButton onClick={() => onToggleReportDetails('sales-categories')}>{expanded === 'sales-categories' ? 'Hide' : 'Show'} Details</DetailsButton>

                {expanded === 'sales-categories' && (
                  <InternalContainer>
                    {reports.map((report, index) => (
                      <BorderContainer key={report.date} border={index < reports.length - 1} paddingBottom={index < reports.length - 1} paddingTop={index > 0}>
                        <SalesCategoryItemContainer>
                          <SectionTitle>{isoToUsDate(report.date)}</SectionTitle>
                          <SectionTitle>{centsToDollarsDisplay(report.salesCategories.reduce((sum, salesCategory) => sum + salesCategory.total, 0))}</SectionTitle>
                        </SalesCategoryItemContainer>

                        {report.salesCategories.filter((salesCategory) => salesCategory.total > 0).length === 0 && <SubtextSmall>No data</SubtextSmall>}

                        {report.salesCategories
                          .filter((salesCategory) => salesCategory.total > 0)
                          .map((salesCategory) => (
                            <SalesCategoryItemContainer key={salesCategory.name}>
                              <SalesCategoryLabel>{salesCategory.name}</SalesCategoryLabel>
                              <SalesCategoryAmount>{centsToDollarsDisplay(salesCategory.total)}</SalesCategoryAmount>
                            </SalesCategoryItemContainer>
                          ))}
                      </BorderContainer>
                    ))}
                  </InternalContainer>
                )}
              </ExternalContainer>

              <ExternalContainer data-cy="checks-no-labor">
                <InternalTitle>Check with No Labor</InternalTitle>

                <InternalSubtitle>
                  A total of <SubtitleEmphasis>{reports.reduce((sum, report) => sum + report.ticketsWithNoLabor.length, 0)}</SubtitleEmphasis> checks were found where labor cannot be found for the assigned employee.
                </InternalSubtitle>

                <DetailsButton onClick={() => onToggleReportDetails('checks-no-labor')}>{expanded === 'checks-no-labor' ? 'Hide' : 'Show'} Details</DetailsButton>

                {expanded === 'checks-no-labor' && (
                  <InternalContainer>
                    {ticketsNoLabor.length === 0 && <SubtextSmall>No data</SubtextSmall>}

                    {ticketsNoLabor.map((ticket, index) => (
                      <BorderContainer key={`${ticket.date}#${ticket.ticketId}`} border={index < reports.length - 1} paddingBottom={index < reports.length - 1} paddingTop={index > 0}>
                        <ChecksNoLaborItemContainer>
                          <SectionTitle>#{ticket.displayId}</SectionTitle>
                          <SectionTitle>{isoToUsDate(ticket.date)}</SectionTitle>
                        </ChecksNoLaborItemContainer>

                        <ChecksNoLaborItemContainer>
                          <SubtextSmall>First Name</SubtextSmall>
                          <SubtextSmall>{ticket.employee.firstName}</SubtextSmall>
                        </ChecksNoLaborItemContainer>

                        <ChecksNoLaborItemContainer>
                          <SubtextSmall>Last Name</SubtextSmall>
                          <SubtextSmall>{ticket.employee.lastName}</SubtextSmall>
                        </ChecksNoLaborItemContainer>

                        <ChecksNoLaborItemContainer>
                          <SubtextSmall>Job Title</SubtextSmall>
                          <SubtextSmall>{ticket.employee.jobTitle || '--'}</SubtextSmall>
                        </ChecksNoLaborItemContainer>
                      </BorderContainer>
                    ))}
                  </InternalContainer>
                )}
              </ExternalContainer>

              <ExternalContainer data-cy="checks-no-owner">
                <InternalTitle>Check with No Owners</InternalTitle>

                <InternalSubtitle>
                  A total of <SubtitleEmphasis>{reports.reduce((sum, report) => sum + report.ticketsWithNoOwner.length, 0)}</SubtitleEmphasis> checks were found where owner cannot be identified.
                </InternalSubtitle>

                <DetailsButton onClick={() => onToggleReportDetails('checks-no-owner')}>{expanded === 'checks-no-owner' ? 'Hide' : 'Show'} Details</DetailsButton>

                {expanded === 'checks-no-owner' && (
                  <InternalContainer>
                    {ticketsNoOwner.length === 0 && <SubtextSmall>No data</SubtextSmall>}

                    {ticketsNoOwner.length > 0 && <SectionTitle>Checks</SectionTitle>}

                    {ticketsNoOwner.map((ticket) => (
                      <ChecksNoOwnerItemContainer key={`${ticket.date}#${ticket.ticketId}`}>
                        <CheckNoOwnerCheckId>#{ticket.displayId}</CheckNoOwnerCheckId>
                        <SubtextMedium>{isoToUsDate(ticket.date)}</SubtextMedium>
                      </ChecksNoOwnerItemContainer>
                    ))}
                  </InternalContainer>
                )}
              </ExternalContainer>

              <ExternalContainer data-cy="unclosed-tickets">
                <InternalTitle>Unclosed Checks</InternalTitle>

                <InternalSubtitle>
                  A total of <SubtitleEmphasis>{unclosedTickets.length}</SubtitleEmphasis> checks were found that were not closed out, totalling {centsToDollarsDisplay(unclosedTickets.reduce((sum, ticket) => sum + ticket.amount, 0))}.
                </InternalSubtitle>

                <DetailsButton onClick={() => onToggleReportDetails('unclosed-tickets')}>{expanded === 'unclosed-tickets' ? 'Hide' : 'Show'} Details</DetailsButton>

                {expanded === 'unclosed-tickets' && (
                  <InternalContainer>
                    {unclosedTickets.length === 0 && <SubtextSmall>No data</SubtextSmall>}

                    {unclosedTickets.map((ticket, index) => (
                      <BorderContainer data-cy="unclosed-ticket-row" key={`${ticket.date}#${ticket.ticketId}`} border={index < unclosedTickets.length - 1} paddingBottom={index < unclosedTickets.length - 1} paddingTop={index > 0}>
                        <UnclosedTicketsItemContainer>
                          <SectionTitle>#{ticket.displayId}</SectionTitle>
                          <SectionTitle>{isoToUsDate(ticket.date)}</SectionTitle>
                        </UnclosedTicketsItemContainer>

                        <UnclosedTicketsItemContainer>
                          <SubtextSmall>Employee</SubtextSmall>
                          <SubtextSmall>{ticket.employee ? `${ticket.employee.firstName} ${ticket.employee.lastName}` : '--'}</SubtextSmall>
                        </UnclosedTicketsItemContainer>

                        <UnclosedTicketsItemContainer>
                          <SubtextSmall>Amount</SubtextSmall>
                          <SubtextSmall>{centsToDollarsDisplay(ticket.amount)}</SubtextSmall>
                        </UnclosedTicketsItemContainer>
                      </BorderContainer>
                    ))}
                  </InternalContainer>
                )}
              </ExternalContainer>

              <ExternalContainer data-cy="auto-clockouts">
                <InternalTitle>Auto Clockouts</InternalTitle>

                <InternalSubtitle>
                  A total of <SubtitleEmphasis>{reports.reduce((sum, report) => sum + report.autoClockedOutLabor.length, 0)}</SubtitleEmphasis> employees were identified as auto clocked out from the POS.
                </InternalSubtitle>

                <DetailsButton onClick={() => onToggleReportDetails('auto-clockouts')}>{expanded === 'auto-clockouts' ? 'Hide' : 'Show'} Details</DetailsButton>

                {expanded === 'auto-clockouts' && (
                  <InternalContainer>
                    {autoClockOuts.length === 0 && <SubtextSmall>No data</SubtextSmall>}

                    {autoClockOuts.map((schedule, index) => (
                      <BorderContainer key={index} border={index < reports.length - 1} paddingBottom={index < reports.length - 1} paddingTop={index > 0}>
                        <AutoClockoutsItemContainer>
                          <AutoClockoutsHeader>{[schedule.firstName, schedule.lastName].filter(Boolean).join(' ')}</AutoClockoutsHeader>

                          <AutoClockoutsHeader>{schedule.schedule.jobTitle}</AutoClockoutsHeader>
                        </AutoClockoutsItemContainer>

                        <AutoClockoutsItemContainer>
                          <SubtextSmall>Clock In</SubtextSmall>
                          <SubtextSmall>{dayjs(schedule.schedule.start).format('MM-DD-YYYY h:mm A')}</SubtextSmall>
                        </AutoClockoutsItemContainer>

                        <AutoClockoutsItemContainer>
                          <SubtextSmall>Clock Out</SubtextSmall>
                          <SubtextSmall>{dayjs(schedule.schedule.end).format('MM-DD-YYYY h:mm A')}</SubtextSmall>
                        </AutoClockoutsItemContainer>

                        <AutoClockoutsItemContainer>
                          <SubtextSmall>Time Worked</SubtextSmall>
                          <SubtextSmall>{new Duration(dayjs(schedule.schedule.end).diff(schedule.schedule.start, 'minutes')).toString()}</SubtextSmall>
                        </AutoClockoutsItemContainer>
                      </BorderContainer>
                    ))}
                  </InternalContainer>
                )}
              </ExternalContainer>
            </HeaderContainer>
          </>
        )}

        {modal === 'result' && !isGeneratingHealthReport && error && (
          <>
            <AlertHeader size={64} />
            <ModalTitle>We are having trouble</ModalTitle>
            <ErrorText>We encountered an issue generating the health report for this site.</ErrorText>
            {error.errorMessages.length && <Message className="message" appearance={Appearance.WARNING} message={error.errorMessages[0]} />}
          </>
        )}
      </Content>
    </ModalFactory>
  );
};

const isoToUsDate = (value: string) => {
  if (!value) {
    return '';
  }

  const [year, month, day] = value.split('-');

  if (!month || !day || !year) {
    return null;
  }

  return `${month}-${day}-${year}`;
};
